import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, useTheme, useMediaQuery, Grid } from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageContainer } from '../components/ui/StyledComponents';
import CreateClassForm from '../components/admin/CreateClassForm';
import CreateStudentForm from '../components/admin/CreateStudentForm';
import CreateGuardianForm from '../components/admin/CreateGuardianForm';
// import CreateParentForm from '../components/admin/CreateParentForm';

// Map of tab indices to their corresponding hash values
const TAB_HASHES = ['#guardian', '#student', '#class'];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

export default function AdminManagementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Initialize tab based on current hash or default to 0
  const getInitialTab = () => {
    const hash = location.hash.toLowerCase();
    const index = TAB_HASHES.indexOf(hash);
    return index !== -1 ? index : 0;
  };

  const [tab, setTab] = useState(getInitialTab);

  // Update hash when tab changes
  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    navigate(TAB_HASHES[newValue], { replace: true });
  };

  // Listen for hash changes
  useEffect(() => {
    const hash = location.hash.toLowerCase();
    const index = TAB_HASHES.indexOf(hash);
    if (index !== -1 && index !== tab) {
      setTab(index);
    }
  }, [location.hash, tab]);

  return (
    <PageContainer>
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(52, 152, 219, 0.2)' 
                : 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonAddIcon 
              sx={{ 
                fontSize: { xs: 24, sm: 28 }, 
                color: theme.palette.primary.main 
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Enrollment & Setup
            </Typography>
            <Typography 
              variant="body2" 
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                mt: 0.5,
              }}
            >
              Create guardians, students, and classes
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Tabs 
            value={tab} 
            onChange={handleTabChange} 
            centered={!isMobile}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile={isMobile}
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 72 },
                padding: { xs: '12px 16px', sm: '12px 24px' },
              },
              mb: { xs: 1, sm: 2 },
            }}
          >
            <Tab label="Create Guardian" />
            <Tab label="Create Student" />
            <Tab label="Create Class" />
          </Tabs>
          <TabPanel value={tab} index={0}>
            <CreateGuardianForm />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <CreateStudentForm />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <CreateClassForm />
          </TabPanel>
        </Grid>
      </Grid>
    </PageContainer>
  );
} 